import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import StartPage from './pages/StartPage';
import StartConfirmationPage from './pages/StartConfirmationPage';
import StartSuccessPage from './pages/StartSuccessPage';
import StartRegisterPage from './pages/StartRegisterPage';
import StartLoginPage from './pages/StartLoginPage';
import ClassesPage from './pages/ClassesPage';
import LanguageSelectionPage from './pages/LanguageSelectionPage';
import AvailabilityPage from './pages/AvailabilityPage';
import ConfirmationPage from './pages/ConfirmationPage';
import SuccessPage from './pages/SuccessPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import StudentsPage from './pages/admin/StudentsPage';
import CreateClassPage from './pages/admin/CreateClassPage';
import ClassViewPage from './pages/admin/ClassViewPage';
import EditClassPage from './pages/admin/EditClassPage';
import ApplyPage from './pages/ApplyPage';
import TutoringTrialPage from './pages/TutoringTrialPage';
import GroupClassPage from './pages/GroupClassPage';
import TutorsGridPage from './pages/TutorsGridPage';
import TutorsPage from './pages/admin/TutorsPage';
import TutorViewPage from './pages/admin/TutorViewPage';
import TutorDetailPage from './pages/TutorDetailPage';
import MandarinClassesLandingPage from './pages/MandarinClassesLandingPage';
import TutorUpdatePage from './pages/TutorUpdatePage';
import TutorDeactivatePage from './pages/TutorDeactivatePage';

// Protected route wrapper (only for pages that REQUIRE authentication)
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#7e55f6] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Main App component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LanguageSelectionPage />} />
      <Route path="/classes" element={<ClassesPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/start/:classTypeId" element={<StartPage />} />
      <Route path="/start-confirmation" element={<StartConfirmationPage />} />
      <Route path="/start-success" element={<StartSuccessPage />} />
      <Route path="/start-register" element={<StartRegisterPage />} />
      <Route path="/start-login" element={<StartLoginPage />} />
      <Route path="/apply" element={<ApplyPage />} />
      <Route path="/tutoring-trial" element={<TutoringTrialPage />} />
      <Route path="/group-classes" element={<GroupClassPage />} />
      <Route path="/tutors" element={<TutorsGridPage />} />
      <Route path="/admin/tutors" element={<TutorsPage />} />
      <Route path="/admin/tutors/:tutorId" element={<TutorViewPage />} />
      <Route path="/tutors/:id" element={<TutorDetailPage />} />
      <Route path="/mandarin" element={<MandarinClassesLandingPage />} />
      <Route path="/tutor-update/:tutorId" element={<TutorUpdatePage />} />
      <Route path="/tutor-deactivate/:tutorId" element={<TutorDeactivatePage />} />

      {/* Protected routes - only these require authentication */}
      <Route 
        path="/availability" 
        element={
          <ProtectedRoute>
            <AvailabilityPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/confirmation" 
        element={
          <ProtectedRoute>
            <ConfirmationPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/success" 
        element={
          <ProtectedRoute>
            <SuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/students" 
        element={
          <ProtectedRoute>
            <StudentsPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/admin/tutors" element={<TutorsPage />} />
      <Route path="/admin/tutors/:tutorId" element={<TutorViewPage />} />
      <Route 
        path="/admin/create-class" 
        element={
          <ProtectedRoute>
            <CreateClassPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/class/:classId" 
        element={
          <ProtectedRoute>
            <ClassViewPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/edit-class/:classId" 
        element={
          <ProtectedRoute>
            <EditClassPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// App wrapped with providers
const App = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-black text-white">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
